import type { Project, SiteContent } from '../types'

export const defaultSiteContent: SiteContent = {
  heroKicker: 'Custom + Spec Homes',
  heroTitle: 'Modern mountain homes,',
  heroAccent: 'crafted with intention.',
  heroBody: 'BlackRock creates refined residences shaped by their setting—with considered architecture, enduring materials, and an exacting attention to detail.',
  heroImage: '/images/projects/donna/01.jpg',
  heroDesktopVideo: '',
  heroMobileVideo: '',
  heroSlides: [],
  heroSlideSeconds: 12,
  statementTitle: 'A home should belong to its landscape—and to the life lived within it.',
  statementBody: 'Black Rock brings a measured, hands-on approach to residential development in Western North Carolina. We pair enduring materials with considered details, creating homes that feel grounded from the first impression to the final finish.',
  workKicker: 'Built portfolio',
  workTitle: 'Residences with a sense of place',
  processTitle: 'Clarity at every step',
  aboutKicker: 'BlackRock Developers',
  aboutTitle: 'Disciplined development. Considered design.',
  aboutBody: 'We develop custom and spec homes with close oversight, trusted trade relationships, and an eye for the details that make a residence feel complete. The result is a calm, accountable experience and a home made for its setting—not simply placed on it.',
  craftImage: '/images/projects/winding/05-great-room.jpg',
  aboutImage: '/images/western-north-carolina.jpg',
  serviceArea: 'Serving communities across Western North Carolina',
  contactTitle: 'Let’s build something enduring.',
  contactBody: 'Tell us about your land, your vision, or the kind of home you are looking for. We’ll start with a straightforward conversation.',
  contactEmail: '',
  contactPhone: '',
  instagramUrl: 'https://www.instagram.com/blackrock.developers.nc/',
}

export const defaultProjects: Project[] = [
  {
    id: '407-melnick-terrace',
    title: '407 Melnick Terrace',
    type: 'Contemporary mountain residence',
    location: 'Asheville, NC 28803',
    year: '2026',
    status: 'Completed',
    summary: 'An elevated one-level home in gated Keswick Hills, pairing dramatic black-and-cedar architecture with a light-filled open plan, stone fireplace, chef’s kitchen, spa-inspired primary suite, and wooded outdoor living.',
    image: '/images/projects/melnick/01-exterior.webp',
    gallery: [
      '/images/projects/melnick/01-exterior.webp',
      '/images/projects/melnick/02-entry.webp',
      '/images/projects/melnick/03-great-room.webp',
      '/images/projects/melnick/04-kitchen.webp',
      '/images/projects/melnick/05-primary-bath.webp',
      '/images/projects/melnick/06-primary-closet.webp',
      '/images/projects/melnick/07-covered-deck.webp',
      '/images/projects/melnick/08-aerial.webp',
    ],
    showcaseImages: ['/images/projects/melnick/01-exterior.webp', '/images/projects/melnick/04-kitchen.webp'],
    highlights: ['4 beds', '3.5 baths', '2,585 sq ft', '1.15 acres'],
    sourceUrl: 'https://www.zillow.com/homedetails/407-Melnick-Ter-Asheville-NC-28803/96345482_zpid/',
    featured: true,
    order: 1,
    isSample: false,
  },
  {
    id: '134-view-ridge-parkway',
    title: '134 View Ridge Parkway',
    type: 'Mountain modern residence',
    location: 'Leicester, NC 28748',
    year: '2026',
    status: 'Completed',
    summary: 'A refined mountain-modern residence in gated Blue Mist Farms, pairing long-range views with a dramatic stone fireplace, quartz waterfall island, spa-inspired finishes, and seamless indoor-outdoor living.',
    image: '/images/projects/view-ridge/01.jpg',
    gallery: [
      '/images/projects/view-ridge/01.jpg',
      '/images/projects/view-ridge/02.jpg',
      '/images/projects/view-ridge/03.jpg',
      '/images/projects/view-ridge/04.jpg',
      '/images/projects/view-ridge/05.jpg',
      '/images/projects/view-ridge/06.jpg',
      '/images/projects/view-ridge/07.jpg',
      '/images/projects/view-ridge/08.jpg',
    ],
    showcaseImages: ['/images/projects/view-ridge/01.jpg', '/images/projects/view-ridge/04.jpg'],
    highlights: ['3 beds', '2.5 baths', '2,665 sq ft', '1.05 acres'],
    sourceUrl: 'https://www.zillow.com/homedetails/134-View-Ridge-Pkwy-Leicester-NC-28748/96346521_zpid/',
    featured: true,
    order: 2,
    isSample: false,
  },
  {
    id: '221-coopers-drive',
    title: '221 Coopers Drive',
    type: 'Contemporary mountain residence',
    location: 'Hendersonville, NC 28739',
    year: '2026',
    status: 'Completed',
    summary: 'A private mountain retreat designed around panoramic views, with soaring glass, a generous wraparound porch, custom cabinetry, smart-home details, and warm contemporary finishes.',
    image: '/images/projects/coopers/01.jpg',
    gallery: [
      '/images/projects/coopers/01.jpg',
      '/images/projects/coopers/02.jpg',
      '/images/projects/coopers/03.jpg',
      '/images/projects/coopers/04.jpg',
      '/images/projects/coopers/05.jpg',
      '/images/projects/coopers/06.jpg',
      '/images/projects/coopers/07.jpg',
      '/images/projects/coopers/08.jpg',
    ],
    showcaseImages: ['/images/projects/coopers/01.jpg', '/images/projects/coopers/03.jpg'],
    highlights: ['4 beds', '3.5 baths', '2,354 sq ft', '1.25 acres'],
    sourceUrl: 'https://www.zillow.com/homedetails/221-Coopers-Dr-Hendersonville-NC-28739/250396127_zpid/',
    featured: true,
    order: 3,
    isSample: false,
  },
  {
    id: '386-donna-lane',
    title: '386 Donna Lane',
    type: 'Contemporary residence',
    location: 'Fairview, NC 28730',
    year: '2025',
    status: 'Completed',
    summary: 'A single-level residence oriented toward long-range mountain views, pairing expansive glazing with a warm, open living core.',
    image: '/images/projects/donna/01.jpg',
    gallery: [
      '/images/projects/donna/01.jpg',
      '/images/projects/donna/03.jpg',
      '/images/projects/donna/04.jpg',
      '/images/projects/donna/06-kitchen.webp',
      '/images/projects/donna/07-primary-bath.webp',
      '/images/projects/donna/08-firepit.webp',
    ],
    showcaseImages: ['/images/projects/donna/01.jpg', '/images/projects/donna/06-kitchen.webp'],
    highlights: ['3 beds', '2.5 baths', '2,007 sq ft', '1.27 acres'],
    sourceUrl: 'https://www.zillow.com/homedetails/386-Donna-Ln-Fairview-NC-28730/96337548_zpid/',
    featured: true,
    order: 4,
    isSample: false,
  },
  {
    id: '25-scott-meadows',
    title: '25 Scott Meadows Drive',
    type: 'Modern residence',
    location: 'Asheville, NC 28806',
    year: '2025',
    status: 'Completed',
    summary: 'A modern Asheville residence with an open main level, sculptural fireplace, private balcony, and nearly an acre of breathing room.',
    image: '/images/projects/scott/01.jpg',
    gallery: [
      '/images/projects/scott/01.jpg',
      '/images/projects/scott/02.jpg',
      '/images/projects/scott/06-kitchen.jpg',
      '/images/projects/scott/07-dining.jpg',
      '/images/projects/scott/03.jpg',
      '/images/projects/scott/08-balcony.jpg',
    ],
    showcaseImages: ['/images/projects/scott/01.jpg', '/images/projects/scott/07-dining.jpg'],
    highlights: ['2 beds + flex', '2.5 baths', '1,760 sq ft', '0.97 acres'],
    sourceUrl: 'https://www.zillow.com/homedetails/25-Scott-Meadows-Dr-Asheville-NC-28806/2053602480_zpid/',
    featured: true,
    order: 5,
    isSample: false,
  },
  {
    id: '63-winding-ridge',
    title: '63 Winding Ridge Road',
    type: 'Mountain contemporary',
    location: 'Fairview, NC 28730',
    year: '2025',
    status: 'Completed',
    summary: 'A richly detailed mountain residence shaped by hardwood, stone, warm wood accents, and year-round views.',
    image: '/images/projects/winding/01.jpg',
    gallery: [
      '/images/projects/winding/01.jpg',
      '/images/projects/winding/04.jpg',
      '/images/projects/winding/05-great-room.jpg',
      '/images/projects/winding/06-kitchen.jpg',
      '/images/projects/winding/08-primary-bath.jpg',
      '/images/projects/winding/09-deck.jpg',
    ],
    showcaseImages: ['/images/projects/winding/01.jpg', '/images/projects/winding/06-kitchen.jpg'],
    highlights: ['3 beds + bonus', '3.5 baths', '2,752 sq ft', '1.36 acres'],
    sourceUrl: 'https://www.zillow.com/homedetails/63-Winding-Ridge-Rd-Fairview-NC-28730/96364096_zpid/',
    featured: true,
    order: 6,
    isSample: false,
  },
]
